import http from '../services/http';

const TEXT_TOOLS_CONFIG_STORAGE_KEY = 'ai_text_tools_configuration_id';

const clampInt = (v, min, max) => {
  const n = Number(v);
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, Math.floor(n)));
};

const getSelectedAiConfigurationIdForTextTools = () => {
  try {
    const raw = localStorage.getItem(TEXT_TOOLS_CONFIG_STORAGE_KEY);
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return !Number.isNaN(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};

const estimateTokensFromChars = (chars) => Math.ceil(Number(chars || 0) / 4);

const computeDynamicMaxTokensForRewrite = (text) => {
  const chars = String(text || '').length;
  const inputTokens = estimateTokensFromChars(chars);
  const desired = Math.max(256, Math.ceil(inputTokens * 1.6));
  return clampInt(desired, 256, 3000);
};

const stripHtml = (html) => {
  const raw = String(html || '');
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const escapeHtml = (text) =>
  String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const plainTextToQuillHtml = (text) => {
  const t = String(text || '').trim();
  if (!t) return '';
  const escaped = escapeHtml(t);
  const paras = escaped
    .split(/\n{2,}/g)
    .map((p) => p.replace(/\n/g, '<br/>'))
    .filter(Boolean);
  return paras.map((p) => `<p>${p}</p>`).join('');
};

export const adjustTextWithGpt = async ({
  text,
  kind = 'texto',
  tone = 'profissional',
  keepLanguage = 'auto',
}) => {
  const input = String(text || '').trim();
  if (!input) return { ok: false, text: '', message: 'Texto vazio' };

  const languageInstruction =
    keepLanguage === 'auto'
      ? 'Mantenha o idioma original do texto.'
      : `Mantenha o idioma em ${keepLanguage}.`;

  const instruction =
    `Você é um revisor de texto. Corrija e melhore o ${kind} abaixo para ficar mais ${tone}, ` +
    `priorizando clareza: o leitor deve entender sem esforço o que foi solicitado e o que foi feito. ` +
    `${languageInstruction} ` +
    `Regras obrigatórias: ` +
    `1) Não resuma e não remova informações. ` +
    `2) Preserve o sentido e mantenha TODAS as perguntas, pedidos e detalhes. ` +
    `3) Não mude o formato (não transforme em lista/passos se o original não for). ` +
    `4) Mantenha o tamanho semelhante ao original (mesma ordem e estrutura geral). ` +
    `5) Apenas corrija ortografia, acentuação, pontuação e clareza; pode ajustar frases, mas sem cortar conteúdo. ` +
    `6) Prefira frases diretas e objetivas; desfaça ambiguidades e trechos confusos, sem inventar informação que não esteja no texto. ` +
    `Retorne apenas o texto final, sem explicações.\n\n` +
    `Texto:\n${input}`;

  const payload = {
    message: instruction,
    history: [],
    max_tokens: computeDynamicMaxTokensForRewrite(input),
    temperature: 0.1,
    // Modo cru: pula prompt base do chat + system_instructions + RAG. Usa apenas
    // as instruções de ajuste de texto do config (text_tools_instructions), se houver.
    raw: true,
  };
  const chosenConfigId = getSelectedAiConfigurationIdForTextTools();
  if (chosenConfigId) payload.ai_configuration_id = chosenConfigId;

  const send = async (p) => {
    const res = await http.post('/ai-assistant/chat', p);
    if (!res?.data?.success) {
      return { ok: false, text: '', message: res?.data?.error || 'Erro ao ajustar texto', meta: res?.data?.meta };
    }
    const out = String(res?.data?.message || '').trim();
    return { ok: true, text: out, meta: res?.data?.meta };
  };

  const first = await send(payload);
  if (!first.ok) return first;

  const finish = String(first.meta?.finish_reason || '').toLowerCase();
  const wasTruncated =
    finish === 'length' ||
    finish === 'max_tokens' ||
    finish === 'maxoutputtokens' ||
    finish === 'max_output_tokens';

  if (wasTruncated) {
    const retry = await send({ ...payload, max_tokens: 3000, temperature: 0.1 });
    if (retry.ok) return retry;
  }

  return first;
};

export const quillAdjustTextWithGpt = async (html, opts = {}) => {
  const plain = stripHtml(html);
  const res = await adjustTextWithGpt({ text: plain, ...opts });
  if (!res.ok) return res;
  return { ok: true, text: plainTextToQuillHtml(res.text) };
};

